import { template as template_b826e5e9763b479c83f1f8e0c66a6b91 } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { isPositive } from 'ramda-adjunct';
import { buildValidations, validator } from '@eflexsystems/ember-tracked-validations';
import BsForm from 'eflex/components/bs-form';
import StandardModal from 'eflex/components/standard-modal';
import { action } from '@ember/object';
@buildValidations({
    goodToReport: [
        validator('presence', true)
    ],
    rejectedToReport: [
        validator('presence', true)
    ],
    scrappedToReport: [
        validator('presence', true)
    ],
    rejectCode: [
        validator('presence', {
            presence: true,
            get disabled () {
                return this.model.noRejectedToReport || !this.model.canSetRejectCodes;
            }
        })
    ],
    scrapCode: [
        validator('presence', {
            presence: true,
            get disabled () {
                return this.model.noScrappedToReport || !this.model.canSetScrapCodes;
            }
        })
    ]
})
class EndActivityModal extends Component {
    @service
    codeRepo;
    @tracked
    notes;
    @tracked
    complete = false;
    @tracked
    rejectCode;
    @tracked
    rejectNotes = '';
    @tracked
    scrapCode;
    @tracked
    scrapNotes = '';
    @tracked
    _goodToReport = 0;
    @tracked
    _rejectedToReport = 0;
    @tracked
    _scrappedToReport = 0;
    get isOperation() {
        return this.args.assemblyOperation ?? false;
    }
    get isMultipiece() {
        return this.args.isStandaloneMultipiece || this.args.assemblyOperation?.operation?.isMultipiece;
    }
    get previousReportedGoodQuantity() {
        if (this.args.isStandaloneMultipiece) {
            return this.args.buildStatusMultipiece.completedQuantity;
        }
        return this.args.assemblyOperation.reportedGoodQuantity;
    }
    get previousRejectedQuantity() {
        if (this.args.isStandaloneMultipiece) {
            return this.args.buildStatusMultipiece.rejectedQuantity;
        }
        return this.args.assemblyOperation.reportedRejectedQuantity;
    }
    get previousScrappedQuantity() {
        if (this.args.isStandaloneMultipiece) {
            return this.args.buildStatusMultipiece.scrappedQuantity;
        }
        return this.args.assemblyOperation.reportedScrappedQuantity;
    }
    get goodToReport() {
        if (this.isMultipiece) {
            return this._goodToReport;
        } else {
            return this.args.assemblyOperation.goodQuantityToReport;
        }
    }
    set goodToReport(val) {
        this._goodToReport = val;
    }
    get rejectedToReport() {
        if (this.isMultipiece) {
            return this._rejectedToReport;
        } else {
            return this.args.assemblyOperation.rejectedQuantityToReport;
        }
    }
    set rejectedToReport(val) {
        this._rejectedToReport = val;
    }
    get noRejectedToReport() {
        return !isPositive(this.rejectedToReport);
    }
    get scrappedToReport() {
        if (this.isMultipiece) {
            return this._scrappedToReport;
        } else {
            return this.args.assemblyOperation.scrappedQuantityToReport;
        }
    }
    set scrappedToReport(val) {
        this._scrappedToReport = val;
    }
    get canSetRejectCodes() {
        if (this.args.isStandaloneMultipiece) {
            return this.args.station.confirmRejects && this.rejectCodes.length > 0;
        }
        return true;
    }
    get noScrappedToReport() {
        return !isPositive(this.scrappedToReport);
    }
    get canSetScrapCodes() {
        if (this.args.isStandaloneMultipiece) {
            return this.args.station.confirmScrapCode && this.scrapCodes.length > 0;
        }
        return true;
    }
    @cached
    get rejectCodes() {
        if (this.args.isStandaloneMultipiece) {
            return this.codeRepo.filteredCodes('reject', {
                station: this.args.station
            });
        }
        return this.codeRepo.filteredCodes('reject', {
            isFromSync: true
        });
    }
    @cached
    get scrapCodes() {
        if (this.args.isStandaloneMultipiece) {
            return this.codeRepo.filteredCodes('scrap', {
                station: this.args.station
            });
        }
        return this.codeRepo.filteredCodes('scrap', {
            isFromSync: true
        });
    }
    get isInvalid() {
        return this.validations?.isInvalid ?? false;
    }
    @action
    handleRejectedToReportChange(value) {
        this.rejectedToReport = value;
        if (this.rejectedToReport === 0) {
            this.rejectCode = null;
            this.rejectNotes = null;
        }
    }
    @action
    handleScrappedToReportChange(value) {
        this.scrappedToReport = value;
        if (this.scrappedToReport === 0) {
            this.scrapCode = null;
            this.scrapNotes = null;
        }
    }
    onConfirm = ()=>{
        this.args.onConfirm({
            notes: this.notes,
            complete: this.complete,
            rejectCode: this.rejectCode,
            rejectNotes: this.rejectNotes,
            scrapCode: this.scrapCode,
            scrapNotes: this.scrapNotes,
            goodToReport: this.goodToReport,
            rejectedToReport: this.rejectedToReport,
            scrappedToReport: this.scrappedToReport
        });
    };
    static{
        template_b826e5e9763b479c83f1f8e0c66a6b91(`
    <StandardModal
      class="end-activity-modal"
      @onConfirm={{this.onConfirm}}
      @onCancel={{@onCancel}}
      @size="md"
      ...attributes
    as |modal|>
      <modal.header class="pb-2" as |header|>
        <div class="d-flex align-items-center w-100">
          <h5 class="modal-title lh-sm">
            {{t "kinetic.endActivity"}}
          </h5>
          {{#unless this.isMultipiece}}
            <header.close />
          {{/unless}}
        </div>
      </modal.header>
      <modal.body>
        <BsForm @model={{this}} as |form|>
          <div class="rounded bg-gray-100 p-3 mb-0">
            <div class="mb-2 fw-bold fs-3">{{t "kinetic.previousQuantity"}}</div>
            <div class="row">
              <div class="form-group col d-flex flex-column">
                <label class="form-label">
                  {{t "kinetic.good"}}
                </label>
                <div class="test-previous-good h6">
                  {{this.previousReportedGoodQuantity}}
                </div>
              </div>
              <div class="form-group col d-flex flex-column">
                <label class="form-label">
                  {{t "rejected"}}
                </label>
                <div class="test-previous-reject h6">
                  {{this.previousRejectedQuantity}}
                </div>
              </div>
              <div class="form-group col d-flex flex-column">
                <label class="form-label">
                  {{t "scrap"}}
                </label>
                <div class="test-previous-scrapped h6">
                  {{this.previousScrappedQuantity}}
                </div>
              </div>
            </div>
          </div>
          <div class="rounded border-1 border-gray-100 p-3 mb-0">
            <div class="mb-2 fw-bold fs-3">{{t "kinetic.quantityToReport"}}</div>
            {{#if this.isMultipiece}}
              <div class="row">
                <div class="col-4">
                  <form.element
                    class="test-good-quantity"
                    @controlType="number"
                    @property="goodToReport"
                    @label={{t "kinetic.good"}}
                  as |el|>
                    <el.control
                      min={{0}}
                    />
                  </form.element>
                </div>
                <div class="col-4">
                  <form.element
                    class="test-rejected-quantity"
                    @controlType="number"
                    @property="rejectedToReport"
                    @onChange={{this.handleRejectedToReportChange}}
                    @label={{t "rejected"}}
                  as |el|>
                    <el.control
                      min={{0}}
                    />
                  </form.element>
                </div>
                <div class="col-4">
                  <form.element
                    class="test-scrapped-quantity"
                    @controlType="number"
                    @property="scrappedToReport"
                    @onChange={{this.handleScrappedToReportChange}}
                    @label={{t "scrap"}}
                  as |el|>
                    <el.control
                      min={{0}}
                    />
                  </form.element>
                </div>
              </div>
            {{else}}
              <div class="row mb-3">
                <div class="form-group col d-flex flex-column">
                  <label class="form-label">
                    {{t "kinetic.good"}}
                  </label>
                  <div class="test-good-to-report h6">
                    {{@assemblyOperation.goodQuantityToReport}}
                  </div>
                </div>
                <div class="form-group col d-flex flex-column">
                  <label class="form-label">
                    {{t "rejected"}}
                  </label>
                  <div class="test-rejected-to-report h6">
                    {{@assemblyOperation.rejectedQuantityToReport}}
                  </div>
                </div>
                <div class="form-group col d-flex flex-column">
                  <label class="form-label">
                    {{t "scrap"}}
                  </label>
                  <div class="test-scrapped-to-report h6">
                    {{@assemblyOperation.scrappedQuantityToReport}}
                  </div>
                </div>
              </div>
            {{/if}}
          </div>

          {{#if this.canSetRejectCodes}}
            <form.element
              class="test-reject-code {{unless this.noRejectedToReport "is-required"}}"
              @controlType="power-select"
              @property="rejectCode"
              @label={{t "kinetic.rejectCode"}}
              @options={{this.rejectCodes}}
              @optionLabelPath="displayName"
              @allowClear={{true}}
            as |el|>
              <el.control
                @placeholder={{t "kinetic.selectRejectCode"}}
                @disabled={{this.noRejectedToReport}}
              />
            </form.element>

            {{#if @isStandaloneMultipiece}}
              <form.element
                class="test-reject-notes mb-3"
                @controlType="textarea"
                @property="rejectNotes"
                @label={{t "rejectNotes"}}
              as |el|>
                <el.control
                  @disabled={{this.noRejectedToReport}}
                />
              </form.element>
            {{/if}}
          {{/if}}

          {{#if this.canSetScrapCodes}}
            <form.element
              class="test-scrap-code {{unless this.noScrappedToReport "is-required"}}"
              @controlType="power-select"
              @property="scrapCode"
              @label={{t "kinetic.scrapCode"}}
              @options={{this.scrapCodes}}
              @optionLabelPath="displayName"
              @allowClear={{true}}
            as |el|>
              <el.control
                @placeholder={{t "kinetic.selectScrapCode"}}
                @disabled={{this.noScrappedToReport}}
              />
            </form.element>

            {{#if @isStandaloneMultipiece}}
              <form.element
                class="test-scrap-notes mb-3"
                @controlType="textarea"
                @property="scrapNotes"
                @label={{t "scrapNotes"}}
              as |el|>
                <el.control
                  @disabled={{this.noScrappedToReport}}
                />
              </form.element>
            {{/if}}
          {{/if}}

          {{#if this.isOperation}}
            <form.element
              class='test-activity-complete mb-3'
              @controlType="checkbox"
              @property="complete"
              @label={{t "kinetic.completeOperation"}}
            />

            <form.element
              class='test-activity-notes mb-3'
              @controlType="textarea"
              @property="notes"
              @label={{t "notes"}}
            />

            {{#if @assemblyOperation.nextAssemblyOp}}
              <div class="form-group d-flex flex-column mb-3">
                <label class="form-label">
                  {{t "kinetic.nextOperation"}}
                </label>
                <div class="next-operation h6">
                  {{@assemblyOperation.nextAssemblyOp.name}}
                </div>
              </div>
            {{/if}}
          {{/if}}

        </BsForm>
      </modal.body>
      <modal.footer as |footer|>
        <footer.confirm @disabled={{this.isInvalid}} />
        {{#unless this.isMultipiece}}
          <footer.close />
        {{/unless}}
      </modal.footer>
    </StandardModal>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
export default EndActivityModal;
